import React from 'react'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

const loaderVariants = cva(
  'relative inline-flex items-center justify-center rounded-full',
  {
    variants: {
      variant: {
        default: 'text-primary',
        secondary: 'text-secondary',
        destructive: 'text-destructive',
        accent: 'text-accent',
        muted: 'text-muted-foreground',
      },
      size: {
        xs: 'h-3 w-3',
        sm: 'h-6 w-6',
        default: 'h-8 w-8',
        lg: 'h-12 w-12',
        xl: 'h-16 w-16',
      },
      ringWidth: {
        thin: 'border',
        default: 'border-2',
        thick: 'border-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      ringWidth: 'default',
    },
  }
)

const containerVariants = cva('flex items-center justify-center', {
  variants: {
    align: {
      horizontal: 'flex-row gap-3',
      vertical: 'flex-col gap-4',
    },
  },
  defaultVariants: {
    align: 'vertical',
  },
})

interface LoaderProps extends VariantProps<typeof loaderVariants> {
  text?: string
  align?: 'horizontal' | 'vertical'
  speed?: number
  isLoading?: boolean
  className?: string
  style?: React.CSSProperties
  showPulse?: boolean
  onAnimationIteration?: () => void
}

const Loader = React.forwardRef<HTMLDivElement, LoaderProps>(
  (
    {
      text,
      variant = 'default',
      size = 'default',
      align = 'vertical',
      speed = 700,
      isLoading = true,
      className,
      style,
      ringWidth = 'default',
      showPulse = true,
      onAnimationIteration,
      ...props
    },
    ref
  ) => {
    const containerClassName = cn(containerVariants({ align }), className)

    const spinnerClassName = cn(loaderVariants({ variant, size, ringWidth }))

    // Calculate inner pulse size based on outer size
    const getPulseSize = () => {
      const sizes = {
        xs: 'h-1.5 w-1.5',
        sm: 'h-2 w-2',
        default: 'h-4 w-4',
        lg: 'h-6 w-6',
        xl: 'h-8 w-8',
      }
      return sizes[size || 'default']
    }

    if (!isLoading) return null

    return (
      <div
        ref={ref}
        className={containerClassName}
        style={style}
        role='status'
        aria-busy={isLoading}
        aria-label='Loading'
        {...props}
      >
        <div className='relative'>
          {/* Spinner */}
          <div
            className={`${spinnerClassName} animate-spin border-current border-t-transparent`}
            style={{ animationDuration: `${speed}ms` }}
            onAnimationIteration={onAnimationIteration}
          />

          {/* Inner pulse */}
          {showPulse && (
            <div
              className={cn(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'animate-pulse rounded-full opacity-75',
                getPulseSize()
              )}
            />
          )}
        </div>

        {/* Text */}
        {text && (
          <span
            className={cn('text-sm font-medium', {
              'text-primary': variant === 'default',
              'text-secondary': variant === 'secondary',
              'text-destructive': variant === 'destructive',
              'text-accent': variant === 'accent',
              'text-muted-foreground': variant === 'muted',
            })}
          >
            {text}
          </span>
        )}
      </div>
    )
  }
)

Loader.displayName = 'Loader'

export { Loader, type LoaderProps }
